import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  USER_OPERATIONS,
  RESOURCE_TYPE,
  API_RESOURCE_URLS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  DATE_FORMAT,
  INTERVAL_TYPE,
} from "../../constants";
import defaultStyles from "./MonthWisePlan.module.css";
import "../../cssModules/switch.css";
import { BackButton } from "..";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useHistory } from "react-router-dom";
import { API } from "../../apis/api";
import { downloadCsv, exportToExcel, getMonthNameWithYear } from "../../utils/utils";
import { buildErrorMessage } from "../../apis/calls";
import moment from "moment";
import EvrNumberToolbar from "../EvrNumberToolbar/EvrNumberToolbar";
import config from "../../config/config";
import PlanUploadErrorsPopup from "../PlanUploadErrorsPopup/PlanUploadErrorsPopup";
import MonthPlanForm from "../MonthPlanForm/MonthPlanForm";

const MonthWisePlan = ({
  setHasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;
  const RESOURCE_OPERATION_UPDATE = USER_OPERATIONS.UPDATE_EVR_QUANTITY;
  const RESOURCE_OPERATION_ADD_CSV = USER_OPERATIONS.ADD_EVR_DATA;
  const { showError,showInternalError, showPopup ,showLoading , closePopUp} = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  
  const history = useHistory();

  const [styles, setStyles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [selectedRow, setSelectedRow] = useState(null); 
  const [asnCount, setAsnCount] = useState(0);
  const [isPlanErrorsOpen, setIsPlanErrorsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState();
  const [activeMonthNameWithYear, setActiveMonthNameWithYear] = useState(
    getMonthNameWithYear(moment())
  );
  const isLucknowProlifePlant = plant?.label === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Parent' : 'VC'
  const setCount = (count) => {
    setAsnCount(count);
  };
  const [showForm, setShowForm] = useState(false);
  const openFormPopup = selectedRow => {
    setShowForm(true);
    setSelectedRow(selectedRow);
  };

  const closeFormPopup = () => {
    setShowForm(false);
  };

  const loadPlanDataOfActiveMonth = () => {
      const selectedMonthNameWithYear = activeMonthNameWithYear.split('-'); // activeMonthNameWithYear will be in form Jan-2021
  
      const selectedMonthName = selectedMonthNameWithYear[0];
      const yearOfSelectedMonth = selectedMonthNameWithYear[1];
  
      const startDate = moment()
        .month(selectedMonthName)
        .year(yearOfSelectedMonth)
        .startOf('month');
      const startDateString = startDate.format(DATE_FORMAT.ISO);
      return getMonthPlanData(startDateString);
    };

  const fixedColumns = [
    {
      width: 160,
      title: `${parentName} Number`,
      field: "vc",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: `${parentName} Description`,
      field: "description",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "Line",
      field: "line",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 240,
      title: "Month Projection Quantity",
      field: "quantity",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: "Dropped",
      field: "dropped",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: "Rolled",
      field: "rolled",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 130,
      title: "SFLT",
      field: "sflt",
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);

  const optionalColumns =[{
      width: 180,
      title: "Balance Quantity",
      field: "completed",
      enableSearch: true,
      enableFilter: true,
    },].filter((c) => c);

  const redirectToApplicationLandingPage = () => {
    history.push("/");
  };

  const getMonthPlanData = async (fromDate) => {
    if (plant.value) {
      setRowsData({ loading: true, ...rowsData });
      const Month= fromDate.substring(0, 7); 
      
      try {
        showLoading();
        let response = await API.get(API_RESOURCE_URLS.VC_MONTHLY_PROJECTION, {
          params: {
            plant: plant.value,
            month: Month,
          },
        });
        const updatedData = response.data.map((item, index) => ({
          ...item,
          id: index + 1 // Serial number starting from 1
        }));
        setRowsData({
          loading: false,
          data: updatedData,
          filteredData: updatedData,
        });
        closePopUp();
      } catch (err) {
        console.log(err);
        showInternalError({ error: err });
      }
    }
  };

  const updateMonthPlanUpload = async(formData) => {
      if(plant.value){
        try{
          const response = await API.patch(API_RESOURCE_URLS.VC_MONTHLY_PROJECTION, formData);
          showPopup({
            type: MESSAGE_TYPE.SUCCESS,
            contextText: ' ',
            info: DISPLAY_MESSAGES.UPDATE_MONTH_PLAN,
          });
          closeFormPopup();
          loadPlanDataOfActiveMonth();
        }catch(error){
          showError({
            contextText: DISPLAY_MESSAGES.UPDATE_MONTH_PLAN_ERR,
            info: error.response?.data?.errors || error.message,
          });
          closeFormPopup();
        }
      }
    };

  const handleDownloadTemplateButtonClick = () => {
    const link = document.createElement('a');
    link.setAttribute('download', `Month-wise-upload-plan-template.xlsx`);
    exportToExcel(config.APPLICATION_VARIANT,INTERVAL_TYPE.MONTH).then(dataUri => {
      link.href = dataUri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

    const handleFileUpload = async file => {
      showLoading();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('plant', plant.label);
  
      try {
        showLoading();
        const response = await API.post(API_RESOURCE_URLS.VC_MONTHLY_PROJECTION, formData);
        response.fileName = file.name;
        closePopUp();
        showPopup({
          response,
          info: 'File Name:' + file.name,
          type: MESSAGE_TYPE.SUCCESS,
          contextText: DISPLAY_MESSAGES.MONTH_PROJECTION_UPLOAD_SUCCESS,
        });
        await loadPlanDataOfActiveMonth(); // refresh data
      } catch (error) {
        if (error.response?.status === 400) {
          setIsPlanErrorsOpen(true);
          console.log(error.response.data,'data');
          
          setUploadErrors(error.response.data || []);
        } else if (error.response?.status === 403) {
          setIsPlanErrorsOpen(true);
          setUploadErrors([{ error: DISPLAY_MESSAGES.PLAN_UPLOAD_FORBIDDEN }]);
        } else {
          showPopup({
            error: error,
            type: MESSAGE_TYPE.FAILURE,
            contextText: DISPLAY_MESSAGES.MONTH_PLAN_UPLOAD_ERROR,
          });
        }
      }
      closePopUp();
    };

  const prepareJsonHeaderAndValueFormats = () => {
  
      const monthPlanHeaderAndValueFormats = [
        {
          label: `${parentName} Number`,
          value: (row) => row.vc,
        },
        {
          label: `${parentName} Description`,
          value: (row) => row.description,
        },
        {
          label: "Plant",
          value: (row) => row.validFrom,
        },
        {
          label: "Line",
          value: (row) => row.validTo,
        },
        {
          label: "Month Projection Quantity",
          value: (row) => row.countryCode,
        },
        {
          label: "Actual Quantity",
          value: (row) => row.completed,
        },
      ].filter((c) => c);
  
      return [...monthPlanHeaderAndValueFormats];
    };
  
    const generateAndDownloadMonthlyProjectionCsvReport = (fetchedData) => {
      try {
        if (!rowsData.loading) {
          const dateValue = moment().format(DATE_FORMAT.ISO);
  
          const jsonData = fetchedData;
  
          const allMonthPlan = [];
  
          const { parseAsync } = require("json2csv");
          const fields = prepareJsonHeaderAndValueFormats(allMonthPlan);
          const opts = { fields };
          parseAsync(jsonData, opts)
            .then((csv) => {
              const exportedFilename =
                "Monthly_Projection_Report_" +
                plant.label +
                "_" +
                dateValue +
                ".csv";
              downloadCsv(csv, exportedFilename);
            })
            .catch((error) =>
              console.error(
                "Failed to download Monthly Projection report, error:" +
                  buildErrorMessage(error)
              )
            );
        }
      } catch (error) {
        console.error(
          "Failed to download Monthly Projection report, error:" +
            buildErrorMessage(error)
        );
      }
    };
    

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);
  
  useEffect(() => {
      loadPlanDataOfActiveMonth();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plant.value, activeMonthNameWithYear]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          action="Monthly Plan"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <EvrNumberToolbar
        plantName={plant.label}
        handleMonthSelect={setActiveMonthNameWithYear}
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={[...fixedColumns,...optionalColumns]}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            primaryAction={{
              name: "Update",
              actionFn: (selectedRow) => openFormPopup(selectedRow),
              authOperation: RESOURCE_OPERATION_UPDATE,
              shouldEnable: (selectedRows) => selectedRows.length === 1,
            }}
            secondaryActions={[
              {
                name: "DOWNLOAD TEMPLATE",
                actionFn: () => handleDownloadTemplateButtonClick(),
              },
              {
                name: "Upload Monthly Plan",
                actionFn: () => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".csv";
                  input.className = styles.input; // Add the className if styles is available in this scope
                  input.id = "upload-csv";
                  input.addEventListener("click", (e) => e.stopPropagation());
                  input.onchange = (e) => {
                    const elem = e.target;
                    handleFileUpload(elem.files[0]);
                    elem.value = ""; // reset input element
                  };

                  input.click();
                },
                authOperation: RESOURCE_OPERATION_ADD_CSV,
              },
              {
                name: "DOWNLOAD CSV",
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_ALL_EVR,
                shouldEnable: () => true,
                actionFn: (filteredData) =>
                  generateAndDownloadMonthlyProjectionCsvReport(filteredData),
              },
            ]}
          />
        </div>
      </div>
      <PlanUploadErrorsPopup
        open={isPlanErrorsOpen}
        handleClose={() => setIsPlanErrorsOpen(false)}
        errors={uploadErrors}
      />
      {showForm && (
        <MonthPlanForm
          closeFormPopup={closeFormPopup}
          updateMonthPlan={updateMonthPlanUpload}
          selectedRow={selectedRow}
          activeSelectedMonth={activeMonthNameWithYear}
        />
      )}
    </>
  );
};

MonthWisePlan.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
MonthWisePlan,
  false,
  RESOURCE_TYPE.EVR_QUANTITY
);
